/*
  usePoseScan — one-shot pose extraction from a golf swing video.

  On mount (or when `videoEl` first becomes ready), this hook:
    1. Loads MediaPipe's PoseLandmarker task (lightweight `_lite` model,
       ~5 MB, cached by the browser after first load).
    2. Runs the video end-to-end at ~24 fps in a hidden pass, calling
       `detectForVideo` on each sampled frame and stashing the 33
       landmarks + normalized world-space coords per timestamp.
    3. Post-processes the frames to detect the "top of backswing" moment
       (highest wrist Y position, image coords are y-inverted) and the
       "impact" frame (lowest right wrist Y AFTER the top). These beat
       Gemini's timestamps when they're clearly wrong.
    4. Reports progress via `status` so the UI can show a scan bar.

  Returns:
    {
      ready:        boolean,        // scan completed
      status:       "idle"|"loading"|"scanning"|"ready"|"error",
      progress:     0..1,           // 0 while loading, 0..1 during scan
      frames:       Array<{t, kp}>, // sorted by t (seconds). kp is 33 { x, y, z, v } (0..1 normalized)
      detected:     { top?: number, impact?: number },  // pose-derived phase timestamps
      landmarksAt:  (t: number) => keypoints | null,    // nearest-frame lookup
      error:        string | null,
    }
*/
import { useEffect, useRef, useState } from "react";

// Public CDN — same URL Google's own examples use. Cached across the app.
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";
// The vision WASM package is auto-fetched by tasks-vision. Version pinned
// to the yarn-installed release so we never fetch a mismatched wasm.
const WASM_ROOT =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm";

// Right wrist landmark index in MediaPipe's 33-point pose model.
export const LM = {
  NOSE: 0,
  LEFT_SHOULDER: 11, RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13, RIGHT_ELBOW: 14,
  LEFT_WRIST: 15, RIGHT_WRIST: 16,
  LEFT_HIP: 23, RIGHT_HIP: 24,
  LEFT_KNEE: 25, RIGHT_KNEE: 26,
  LEFT_ANKLE: 27, RIGHT_ANKLE: 28,
};

// Cache the loaded PoseLandmarker across usePoseScan invocations so a
// user re-opening the Studio doesn't repay the 5-MB model download.
let _landmarkerPromise = null;
async function loadLandmarker() {
  if (_landmarkerPromise) return _landmarkerPromise;
  _landmarkerPromise = (async () => {
    const { FilesetResolver, PoseLandmarker } = await import("@mediapipe/tasks-vision");
    const fileset = await FilesetResolver.forVisionTasks(WASM_ROOT);
    return PoseLandmarker.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
      runningMode: "VIDEO",
      numPoses: 1,
      minPoseDetectionConfidence: 0.4,
      minPosePresenceConfidence: 0.4,
      minTrackingConfidence: 0.4,
    });
  })();
  return _landmarkerPromise;
}

const FPS = 24;              // sample rate for the offline scan
const MAX_SCAN_SECONDS = 15; // ignore anything past 15s — swings are short

export function usePoseScan(videoEl) {
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [frames, setFrames] = useState([]);
  const [detected, setDetected] = useState({});
  const [error, setError] = useState(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    if (!videoEl) return undefined;

    // Wait until metadata is available so `duration` is known.
    const start = async () => {
      try {
        if (!videoEl.duration || Number.isNaN(videoEl.duration)) {
          await new Promise((r) => {
            videoEl.addEventListener("loadedmetadata", r, { once: true });
          });
        }
        if (cancelRef.current) return;
        setStatus("loading");
        setProgress(0);
        const landmarker = await loadLandmarker();
        if (cancelRef.current) return;
        setStatus("scanning");

        const dur = Math.min(videoEl.duration || 0, MAX_SCAN_SECONDS);
        if (dur <= 0.1) { setStatus("error"); setError("Video too short"); return; }
        const step = 1 / FPS;
        // Freeze playback controls during scan
        const prevPaused = videoEl.paused;
        const prevMuted = videoEl.muted;
        videoEl.pause();
        videoEl.muted = true;

        // Seek + detect loop. `seeked` fires once per programmatic
        // currentTime change; we `await` it to guarantee the browser has
        // rasterised the new frame before running the pose detector.
        const collected = [];
        const seekTo = (t) => new Promise((resolve) => {
          const onSeeked = () => {
            videoEl.removeEventListener("seeked", onSeeked);
            resolve();
          };
          videoEl.addEventListener("seeked", onSeeked);
          videoEl.currentTime = Math.min(t, videoEl.duration - 0.01);
        });

        let t = 0;
        while (t <= dur) {
          if (cancelRef.current) return;
          await seekTo(t);
          // detectForVideo expects a monotonically increasing timestamp
          // in *milliseconds*. Use ms since scan-start.
          const tsMs = Math.round(t * 1000);
          let res;
          try {
            res = landmarker.detectForVideo(videoEl, tsMs);
          } catch {
            res = null;
          }
          const lm = res?.landmarks?.[0] || null;
          if (lm) collected.push({ t, kp: lm });
          setProgress(Math.min(1, t / dur));
          t += step;
        }

        // Restore video state
        videoEl.muted = prevMuted;
        videoEl.currentTime = 0;
        if (!prevPaused) videoEl.play().catch(() => {});

        // Derive top-of-backswing (highest wrist — smallest y since image
        // coords are top-left origin) and impact (lowest wrist AFTER top).
        // We use the right wrist by default; if visibility is low, we
        // fall back to the left wrist.
        const wristIdx = (() => {
          const rv = collected.reduce((s, f) => s + (f.kp[LM.RIGHT_WRIST]?.visibility || 0), 0);
          const lv = collected.reduce((s, f) => s + (f.kp[LM.LEFT_WRIST]?.visibility || 0), 0);
          return rv >= lv ? LM.RIGHT_WRIST : LM.LEFT_WRIST;
        })();
        let topFrame = null;
        let topY = Infinity;
        collected.forEach((f) => {
          const w = f.kp[wristIdx];
          if (!w) return;
          if (w.y < topY) { topY = w.y; topFrame = f; }
        });
        // Impact: after top, find the frame where wrist y is at max
        // (closest to the ground) — approximates the impact position.
        let impactFrame = null;
        let impactY = -Infinity;
        if (topFrame) {
          collected.forEach((f) => {
            if (f.t <= topFrame.t) return;
            const w = f.kp[wristIdx];
            if (!w) return;
            if (w.y > impactY) { impactY = w.y; impactFrame = f; }
          });
        }

        if (cancelRef.current) return;
        setFrames(collected);
        setDetected({
          top: topFrame?.t,
          impact: impactFrame?.t,
        });
        setStatus("ready");
        setProgress(1);
      } catch (e) {
        console.warn("Pose scan failed:", e);
        setError(e?.message || "Pose scan failed");
        setStatus("error");
      }
    };
    start();
    return () => { cancelRef.current = true; };
  }, [videoEl]);

  // Nearest-frame lookup. Binary search would be nicer for very long
  // clips; for a 3-15s golf swing at 24 fps this is 360 items max — a
  // linear scan is fine and easier to audit.
  const landmarksAt = (t) => {
    if (!frames.length) return null;
    let best = frames[0];
    let bestDiff = Math.abs(frames[0].t - t);
    for (let i = 1; i < frames.length; i += 1) {
      const d = Math.abs(frames[i].t - t);
      if (d < bestDiff) { best = frames[i]; bestDiff = d; }
    }
    return best.kp;
  };

  return {
    ready: status === "ready",
    status,
    progress,
    frames,
    detected,
    landmarksAt,
    error,
  };
}
